import React, {
  PropsWithChildren,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { Log as OidcLog, User, WebStorageStateStore } from "oidc-client-ts";
import {
  AuthProviderProps,
  AuthProvider as OidcAuthProvider,
  useAuth,
} from "react-oidc-context";
import { useDispatch } from "react-redux";
import {
  Language,
  updateAuthContext,
  updateCustomerInformationForSupportTicket,
  updateLanguage,
  updateTimezone,
} from "../reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { toISO2 } from "@src/cloud-connect/user-manager/utils/to-iso2";
import { getAuthContext } from "./use-auth-context";
import { useTokenExpiryCheck } from "@src/hooks/use-token-expiry-check";
import {
  readAuthServiceEnvVar,
  readAuthAuthorityEnvVar,
  readAuthClientIdEnvVar,
} from "@src/cloud-connect/feature-toggles/feature-toggles";

const AUTH_SERVICE_ZITADEL = "zitadel";
const ZITADEL_SCOPES =
  "openid profile email urn:zitadel:iam:user:metadata urn:zitadel:iam:user:resourceowner urn:zitadel:iam:org:project:id:zitadel:aud";

export function enableOidcLogging(): void {
  OidcLog.setLogger(console);
}

/**
 * Handles the callback after successful sign-in
 * Removes auth payload from URL to prevent issues with token renewal
 */
const handleSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const createBaseOidcConfig = (): AuthProviderProps => ({
  authority: readAuthAuthorityEnvVar(),
  client_id: readAuthClientIdEnvVar(),
  redirect_uri: `${window.location.origin}/login`,
  onSigninCallback: handleSigninCallback,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  loadUserInfo: true,
  // tokens are valid 24h and we want exactly one first login:
  // no background renewal, the token-expiry check handles the end of the session
  automaticSilentRenew: false,
});

/**
 * Adds auth service-specific scopes to the OIDC config
 */
const addAuthServiceScopes = (config: AuthProviderProps): AuthProviderProps => {
  const authService = readAuthServiceEnvVar();

  if (authService === AUTH_SERVICE_ZITADEL) {
    return {
      ...config,
      scope: ZITADEL_SCOPES,
    };
  }

  return config;
};

const LANGUAGE_BY_CODE: Record<string, Exclude<Language, null>> = {
  en: "en",
  eng: "en",
  de: "de",
  deu: "de",
  zh: "zh",
  zho: "zh",
  it: "it",
  ita: "it",
  es: "es",
  spa: "es",
  pt: "pt",
  por: "pt",
  nl: "nl",
  nld: "nl",
  tr: "tr",
  tur: "tr",
};

const parseLanguage = (language: string | null | undefined): Language | null => {
  const code = language?.trim().toLowerCase().split(/[-_]/)[0];
  return code ? (LANGUAGE_BY_CODE[code] ?? null) : null;
};

/**
 * Component that tracks authentication state changes and updates Redux store
 */
const AuthTracker = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const authUserId = auth.user?.profile.sub ?? null;
  const authSessionRef = useRef({ userId: authUserId, generation: 0 });

  useLayoutEffect(() => {
    const session = authSessionRef.current;
    if (session.userId === authUserId) return;

    authSessionRef.current = {
      userId: authUserId,
      generation: session.generation + 1,
    };
    dispatch(updateAuthContext(null));

    if (session.userId && session.userId !== authUserId) {
      sessionStorage.removeItem("user-language");
    }
  }, [authUserId, dispatch]);

  const updateAuthState = useCallback(
    async (user: User) => {
      const userId = user.profile.sub;
      const session = authSessionRef.current;
      const isCurrentSession = () =>
        authSessionRef.current.userId === userId &&
        authSessionRef.current.generation === session.generation;

      if (!isCurrentSession()) return;

      const context = await getAuthContext(user);
      if (!isCurrentSession()) return;

      const savedLanguage = sessionStorage.getItem("user-language") as Language;

      if (!context) {
        console.error("Failed to get auth context for user", userId);
        return;
      }

      const tokenLanguage =
        parseLanguage(user.profile?.locale as string) ?? "en";
      const sessionLanguage = parseLanguage(savedLanguage);
      dispatch(updateLanguage(sessionLanguage ?? tokenLanguage));

      dispatch(updateAuthContext(context));
      dispatch(
        updateCustomerInformationForSupportTicket(
          context.userInfoForSupportTicket,
        ),
      );

      // Load the account's saved language on first login; an explicit session
      // choice takes precedence over the account preference.
      getSingleUserUseCase(context.userId)
        .then((userResult) => {
          if (!isCurrentSession()) return;

          if (userResult.success && userResult.data?.chosen_timezone) {
            dispatch(updateTimezone(userResult.data.chosen_timezone));
          }

          const sessionLanguage = parseLanguage(
            sessionStorage.getItem("user-language"),
          );
          const accountLanguage =
            userResult.success && userResult.data?.language
              ? toISO2(userResult.data.language)
              : null;
          const language = sessionLanguage ?? accountLanguage ?? tokenLanguage;

          sessionStorage.setItem("user-language", language);
          dispatch(updateLanguage(language));
        })
        .catch(() => {
          if (!isCurrentSession()) return;

          const sessionLanguage = parseLanguage(
            sessionStorage.getItem("user-language"),
          );
          if (sessionLanguage) return;

          sessionStorage.setItem("user-language", tokenLanguage);
          dispatch(updateLanguage(tokenLanguage));
        });
    },
    [dispatch],
  );

  useEffect(() => {
    const savedLanguage = sessionStorage.getItem("user-language") as Language;
    if (savedLanguage) {
      dispatch(updateLanguage(savedLanguage));
    }
  }, [dispatch]);

  // Handle auth state
  useEffect(() => {
    if (auth.user) {
      updateAuthState(auth.user);
    }

    // subscribe to user loaded events for token renewals
    const unsubscribe = auth.events.addUserLoaded((user: User) => {
      updateAuthState(user);
    });

    return unsubscribe;
  }, [auth.user, auth.events, updateAuthState]);

  // centralized token expiry check (see use-token-expiry-check.ts)
  useTokenExpiryCheck();

  return <>{children}</>;
};

/**
 * Root authentication provider component
 * Wraps the application with OIDC authentication and state tracking
 */
export const AuthProvider = ({ children }: PropsWithChildren) => {
  const oidcConfig = addAuthServiceScopes(createBaseOidcConfig());

  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthTracker>{children}</AuthTracker>
    </OidcAuthProvider>
  );
};
